/* global mixpanel */
import { put, call, select } from "redux-saga/effects"
import { delay } from 'redux-saga'
import * as actions from "../action_creators"
import * as client from "../../api/client"
import { sendGraphqlRequest } from "../../api/graphQlClient"
import { getProfile, getUI, getNavigation } from "../selectors"
import * as jwtUtils from "../../util/jwt_utils"
import { GET_PROFILE } from "../../util/profile_graphql_queries"
import { convertProfileRemoteToRedux } from "../../util/redux_utils"
import { ingressAccountAndValidCustomer } from "../../util/account_customer_utils"
import { getNextOnboardingStep } from "../../util/profile_utils"
import { makeToast } from "../../util/toast_util"
import { handleBootstrap } from "./bootstrap_sagas"
import { alerts } from "../../util/alert_error_utils"
import { handleSignout } from "./session_saga"
import { TERMS_VERSION } from "../../common/constants/app_consts"
import { GET_ACCOUNT_CUSTOMER } from "../../util/customer_graphql_queries"
import { sitka } from "../store"
import history from '../history'

export function* handleGetProfile() {
    const { profile: resp } = yield call(sendGraphqlRequest, GET_PROFILE)

    if (resp === undefined) return
    const {
        account,
        teams,
        memberships: rawMemberships,
        roles,
        teamMembers: rawTeamMembers,
        ...profile
    } = resp
    const membershipsToProcess = rawMemberships
        ? rawMemberships.constructor !== Array
            ? [rawMemberships]
            : rawMemberships
        : []
    yield !profile.verified &&
        put(actions.setAlerts("notVerified", "dashboard"))
    const memberships = membershipsToProcess.reduce(
        (acc, m) => ({ ...acc, [m.teamId]: m }),
        [],
    )

    // configure oauth
    sitka.getModules().oauth.handleIngressOAuthTokens(profile.oAuthTokens)
    sitka.getModules().webhookSubscriptions.handleIngressWebhookSubscriptions(profile.webhookSubscriptions)

    // configure team members
    const teamMembers = rawTeamMembers.reduce((acc, m) => {
        return m ? { ...acc, [m.id]: m } : acc
    }, [])

    const onboardingStep = profile.onboardingStep ? profile.onboardingStep : "1"
    yield put(
        actions.receiveProfile({
            memberships,
            teamMembers,
            role: [roles],
            ...profile,
            onboardingStep,
        }),
    )
    if (teams) {
        yield put(actions.receiveTeams(teams))
    }

    // get account
    if (account.id) {
        const customerResp = yield call(
            sendGraphqlRequest,
            GET_ACCOUNT_CUSTOMER(account.id),
        )
        yield call(ingressAccountAndValidCustomer, customerResp.account)
    }

    mixpanel.identify(profile.id)
}

export function* handleAgreeToTerms() {
    const { id, notificationPreferences } = yield select(getProfile)
    const meta = {
        id,
        termsVersion: TERMS_VERSION,
        ...notificationPreferences,
    }
    try {
        yield call(client.patchUser, meta)
    } catch (e) {
        const { msg } = alerts["failAgreeToTerms"]
        yield call(makeToast, msg, "danger")
    }
}

export function* handleUpdateUserName({ name: fullName }) {
    const profile = yield select(getProfile)

    const meta = {
        id: profile.id,
        fullName,
        ...profile.notificationPreferences,
    }

    try {
        const resp = yield call(client.patchUser, meta)
        if (resp === undefined) {
            yield put(actions.setAlerts("failProfileUpdate", ""))
            return
        }

        const { fullName } = resp
        const profileIngressShape = convertProfileRemoteToRedux(profile)

        // update local state profile
        yield put(
            actions.receiveProfile({
                ...profileIngressShape,
                fullName,
            }),
        )
        // only alert if onboarding is complete
        if (profile.onboardingStep === "done") {
            // set alert
            yield put(actions.setAlerts("successProfileUpdate", ""))
        }
    } catch (e) {
        yield put(actions.setAlerts("failProfileUpdate", ""))
        return
    }
}

export function* handleUpdateUserTimezone({timezone}) {
    const profile = yield select(getProfile)

    const meta = {
        id: profile.id,
        timezone,
        ...profile.notificationPreferences,
    }

    try {
        const resp = yield call(client.patchUser, meta)
        if (resp === undefined) {
            yield put(actions.setAlerts("failProfileUpdate", ""))
            return
        }

        const { timezone: tz } = resp
        const profileIngressShape = convertProfileRemoteToRedux(profile)

        // update local state profile
        yield put(
            actions.receiveProfile({
                ...profileIngressShape,
                timezone: tz,
            }),
        )

        // set alert
        yield put(actions.setAlerts("successProfileUpdate", ""))
    } catch (e) {
        yield put(actions.setAlerts("failProfileUpdate", ""))
        return
    }
}

export function* handleChangePassword({ currentPassword, newPassword }) {
    const profile = yield select(getProfile)
    const ui = yield select(getUI)

    try {
        const resp = yield call(client.changePassword, {
            email: profile.email.address,
            password: newPassword,
            oldPassword: currentPassword,
        })
        // handle getting and setting new jwt
        if (resp === undefined) {
            return
        }
        yield put(actions.setUiPwModalOpen(false))
        yield call(jwtUtils.setToken, resp.id)
        yield put(actions.setAlerts("passwordChanged", ""))
    } catch (e) {
        yield put(actions.setAlerts("incorrectCurrentPassword", ""))
        return
    }

    // set ui
    yield put(actions.setUI({ ...ui, clearSettingsPassword: true }))

    // set it back
    yield put(actions.setUI({ ...ui, clearSettingsPassword: false }))
}

export function* handleForgotPassword({ email }) {
    const resp = yield call(client.forgotPassword, email)
    if (resp === true) {
        const ui = yield select(getUI)
        // set ui
        yield put(actions.setUI({ ...ui, clearSettingsPassword: true }))
        // set it back
        yield put(actions.setUI({ ...ui, clearSettingsPassword: false }))
    } else {
        const { msg } = alerts["forgotPasswordError"]
        yield delay(800)
        yield call(makeToast, msg, "danger")
    }
}

export function* handleIncrementOnboardingStep() {
    const profile = yield select(getProfile)
    const { onboardingStep } = profile
    const nextOnboardingStep = getNextOnboardingStep(
        onboardingStep,
    )
    
    const meta = {
        id: profile.id,
        onboardingStep: nextOnboardingStep,
        ...profile.notificationPreferences,
    }

    // patch onboarding step
    try {
        const resp = yield call(client.patchUser, meta)
        if (resp === undefined) {
            return
        }
        const newProfile = convertProfileRemoteToRedux({
            ...profile,
            onboardingStep: resp.onboardingStep,
        })
        yield put(actions.receiveProfile(newProfile))
        if (nextOnboardingStep === "done") {
            yield put(actions.setCurrentInvite(null))
            
            // If any, redirect now
            const navigation = yield select(getNavigation)
            const { redirect, location } = navigation
            if (redirect) {
                yield call(history.push, location.pathname)
            }
        }
    } catch (e) {
        return
    }
}

export function* handleVerifyEmail({ code, history: hist }) {
    try {
        const resp = yield call(client.verifyEmail, `?verificationCode=${code}`)
        // handle already verified account
        if (resp.status === 204) {
            // alert user account has already been verified
            const { msg } = alerts["alreadyVerified"]
            yield delay(800)
            yield call(makeToast, msg, "danger")
            yield delay(1000)
            // clear token
            yield call(handleSignout)
            // navigate to sign in
            yield call(hist.push, "/signIn")
            return
        }
        const { msg } = alerts["successAccountVerification"]
        yield call(makeToast, msg, "success")
        yield call(handleBootstrap)
        yield delay(800)
        yield call(handleIncrementOnboardingStep, {})
    } catch (e) {
        yield delay(800)
        const { msg } = alerts["failAccountVerification"]
        yield call(makeToast, msg, "danger")
    }
}

export function* handleUpdateNotificationPreferences({ notificationPreferences }) {
    const profile = yield select(getProfile)
    const { id } = profile

    const meta = {
        id,
        ...notificationPreferences,
    }
    try {
        const resp = yield call(client.patchUser, meta)
        if (resp === undefined) {
            yield put(actions.setAlerts("failProfileUpdate", ""))
            return
        }

        const profileIngressShape = convertProfileRemoteToRedux({
            ...profile,
            notificationPreferences,
        })

        // update local state profile
        yield put(
            actions.receiveProfile({
                ...profileIngressShape,
            }),
        )

        // set alert
        yield put(actions.setAlerts("successProfileUpdate", ""))
    } catch (e) {
        yield put(actions.setAlerts("failProfileUpdate", ""))
        return
    }
}



// WEBPACK FOOTER //
// ./src/redux/sagas/profile_sagas.js